var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, property, ReactiveElement, state } from '@folkjs/dom/ReactiveElement';
import { brushInkShape, brushToDeleteElements } from './interactions/brush';
import { clickToCreateArrow, clickToCreateEventPropagator } from './interactions/connection';
import { dragToCreateShape } from './interactions/create-element';
const delay = (ms = 0) => new Promise((r) => setTimeout(r, ms));
export class FolkInstrument extends ReactiveElement {
    constructor() {
        super(...arguments);
        this.type = '';
        this.#interaction = undefined;
    }
    static { this.tagName = 'folk-instrument'; }
    static #interactionMap = new Map([
        ['select', async () => { }],
        [
            'erase',
            async (container, cancellationSignal) => {
                await brushToDeleteElements(container, cancellationSignal, (el) => el.folkShape !== undefined && el.parentElement === container ? el : null);
            },
        ],
        [
            'rectangle',
            async (container, cancellationSignal) => {
                await dragToCreateShape(container, cancellationSignal, () => document.createElement('div'));
            },
        ],
        [
            'draw',
            async (container, cancellationSignal) => {
                await brushInkShape(container, cancellationSignal);
            },
        ],
        [
            'text',
            async (container, cancellationSignal) => {
                const el = await dragToCreateShape(container, cancellationSignal, () => {
                    const div = document.createElement('div');
                    div.contentEditable = 'true';
                    return div;
                });
                if (el) {
                    await delay();
                    el.focus();
                }
            },
        ],
        [
            'arrow',
            async (container, cancellationSignal) => {
                await clickToCreateArrow(container, cancellationSignal);
            },
        ],
        [
            'event-propagator',
            async (container, cancellationSignal) => {
                await clickToCreateEventPropagator(container, cancellationSignal);
            },
        ],
    ]);
    static setInteraction(type, interaction) {
        this.#interactionMap.set(type, interaction);
    }
    static getInteraction(type) {
        return this.#interactionMap.get(type);
    }
    #interaction;
    get interaction() {
        return this.#interaction;
    }
    createRenderRoot() {
        const root = super.createRenderRoot();
        root.appendChild(document.createElement('slot'));
        return root;
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('type'))
            this.#interaction = this.constructor.getInteraction(this.type);
    }
}
__decorate([
    property({ type: String })
], FolkInstrument.prototype, "type", void 0);
export class FolkToolbar extends ReactiveElement {
    constructor() {
        super(...arguments);
        this.activeInstrument = 'select';
        this.locked = false;
        this.container = '';
        this.containerEl = null;
        this.#fieldset = document.createElement('fieldset');
        this.#cancelInstrument = null;
        this.#onInput = (e) => {
            this.activeInstrument = e.target.value;
        };
        this.#onKeydown = (e) => {
            if (e.key === 'Escape' && this.activeInstrument !== 'select') {
                e.preventDefault();
                e.stopImmediatePropagation();
                e.stopPropagation();
                this.activeInstrument = 'select';
            }
            4; // TODO: think about how to be more specific here
            if (e.code.startsWith('Digit') && (document.activeElement === document.body || document.activeElement === this)) {
                e.preventDefault();
                e.stopImmediatePropagation();
                e.stopPropagation();
                const instrument = this.renderRoot.querySelector(`label:nth-child(${e.key}) input[type="radio"]`);
                if (instrument)
                    this.activeInstrument = instrument.value;
            }
        };
    }
    static { this.tagName = 'folk-toolbar'; }
    static { this.shadowRootOptions = {
        ...ReactiveElement.shadowRootOptions,
        slotAssignment: 'manual',
    }; }
    static { this.styles = css `
    :host {
      display: block;
    }

    fieldset {
      display: flex;
      position: relative;
      flex-wrap: nowrap;
      border: 1px;
      padding: 0;
      margin: 0;
      border-radius: 11px;
      background-color: white;
      box-shadow:
        0px 0px 2px hsl(0, 0%, 0%, 16%),
        0px 2px 3px hsl(0, 0%, 0%, 24%),
        0px 2px 6px hsl(0, 0%, 0%, 0.1),
        inset 0px 0px 0px 1px hsl(0, 0%, 100%);
      z-index: 0;

      label {
        white-space: nowrap;
        position: relative;
        padding: 1rem 0.75rem;

        &:hover {
          cursor: pointer;
        }

        &:has(input[type='radio']:checked) {
          color: white;
        }
      }

      input[type='radio'] {
        -webkit-appearance: none;
        appearance: none;
        all: unset;
        /* For iOS < 15 to remove gradient background */
        background-color: transparent;
        /* Not removed via appearance */
        margin: 0;

        position: absolute;
        inset: 5px;
        z-index: 3;
        border-radius: 8px;

        &:hover {
          background-color: var(--folk-instrument-bg, hsl(0, 0%, 0%, 4.3%));
          cursor: pointer;
        }

        &:checked {
          z-index: 1;
          background-color: var(--folk-instrument-bg, hsl(214, 84%, 56%));
        }
      }
    }

    ::slotted(*) {
      position: relative;
      z-index: 2;
    }
  `; }
    #fieldset;
    #cancelInstrument;
    createRenderRoot() {
        const root = super.createRenderRoot();
        this.#fieldset.part.add('fieldset');
        this.#fieldset.addEventListener('input', this.#onInput);
        root.appendChild(this.#fieldset);
        // TODO: use mutation observer to watch changes to slots
        this.querySelectorAll('folk-instrument[type]').forEach((el, i) => {
            const label = document.createElement('label');
            label.part.add('label');
            const slot = document.createElement('slot');
            slot.name = el.getAttribute('type');
            const input = document.createElement('input');
            input.type = 'radio';
            input.name = 'instrument';
            input.value = el.getAttribute('type');
            input.part.add('input');
            if (i === 0)
                input.checked = true;
            label.append(slot, input);
            this.#fieldset.appendChild(label);
            slot.assign(el);
        });
        return root;
    }
    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('keydown', this.#onKeydown);
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('container')) {
            this.containerEl = document.querySelector(this.container);
        }
        if (changedProperties.has('activeInstrument')) {
            const input = this.renderRoot.querySelector(`input[type="radio"][value="${this.activeInstrument}"]`);
            if (input) {
                input.checked = true;
                input.focus();
                this.#startInstrument(this.activeInstrument);
            }
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('keydown', this.#onKeydown);
    }
    #onInput;
    #onKeydown;
    async #startInstrument(activeInstrument) {
        this.#cancelInstrument?.abort();
        this.#cancelInstrument = null;
        const folkInstrument = this.querySelector(`folk-instrument[type="${activeInstrument}"]`);
        if (!folkInstrument?.interaction || this.containerEl === null)
            return;
        this.#cancelInstrument = new AbortController();
        await folkInstrument.interaction(this.containerEl, this.#cancelInstrument.signal);
        this.#cancelInstrument = null;
        if (this.locked) {
            // request an update for the same instrument
            this.requestUpdate('activeInstrument');
        }
        else {
            // Change detection should stop infinite loops from happening
            this.activeInstrument = 'select';
        }
    }
}
__decorate([
    property({ type: String, reflect: true })
], FolkToolbar.prototype, "activeInstrument", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FolkToolbar.prototype, "locked", void 0);
__decorate([
    property({ type: String, reflect: true })
], FolkToolbar.prototype, "container", void 0);
__decorate([
    state()
], FolkToolbar.prototype, "containerEl", void 0);
//# sourceMappingURL=folk-toolbar.js.map